import { computed, getCurrentInstance } from 'vue';

export const DEFAULT_LOCALE = 'en';
export const DEFAULT_DATE_FORMAT = 'yyyy-MM-dd';

export function toExcelDateFormat(dateFnsFormat: string): string {
  return dateFnsFormat.toLowerCase();
}

export function decimalSeparator(locale: string): string {
  const parts = new Intl.NumberFormat(locale).formatToParts(1.1);
  return parts.find((part) => part.type === 'decimal')?.value ?? '.';
}

export function csvDelimiterFor(locale: string): string {
  return decimalSeparator(locale) === ',' ? ';' : ',';
}

const csvNumberFormatters = new Map<string, Intl.NumberFormat>();

export function csvNumber(value: number, locale: string): string {
  let formatter = csvNumberFormatters.get(locale);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, {
      useGrouping: false,
      maximumFractionDigits: 20,
    });
    csvNumberFormatters.set(locale, formatter);
  }
  return formatter.format(value);
}

interface AppI18n {
  locale: string;
  dateFnsDateFormat: string;
  formatNumber(value: any, opts?: NumberFormatOpts): string;
  formatDate(value: any): string;
}

export interface NumberFormatOpts {
  decimals?: number;
  minDecimals?: number;
}

export function withoutFloatNoise(value: any): any {
  const number = typeof value === 'number' ? value : Number(value);
  if (value == null || value === '' || !Number.isFinite(number)) {
    return value;
  }
  const cleaned = Number(number.toPrecision(13));
  return Math.abs(cleaned) < 1e-10 ? 0 : cleaned;
}

export function useGridI18n() {
  const instance = getCurrentInstance();
  const i18n = (): AppI18n | null =>
    (instance?.proxy as any)?.$app?.i18n ?? null;

  const locale = computed(() => i18n()?.locale || DEFAULT_LOCALE);
  const dateFormat = computed(
    () => i18n()?.dateFnsDateFormat || DEFAULT_DATE_FORMAT,
  );
  const excelDateFormat = computed(() => toExcelDateFormat(dateFormat.value));

  function formatNumber(value: any, opts?: NumberFormatOpts): string {
    value = withoutFloatNoise(value);
    const app = i18n();
    if (app) {
      return app.formatNumber(value, opts);
    }
    if (value == null || !Number.isFinite(Number(value))) {
      return value == null ? '' : String(value);
    }
    return Number(value).toLocaleString(locale.value, {
      maximumFractionDigits: opts?.decimals ?? 20,
      minimumFractionDigits: opts?.decimals ?? opts?.minDecimals,
    });
  }

  function formatDate(value: any): string {
    const app = i18n();
    if (app) {
      return app.formatDate(value);
    }
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime())
      ? String(value)
      : date.toISOString().slice(0, 10);
  }

  return { locale, dateFormat, excelDateFormat, formatNumber, formatDate };
}
